import { ReactNode, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { api } from '../../services/api';
import { Panel, PanelHeader, PanelBody } from '../ui/Panel';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import MoreMenu from '../ui/MoreMenu';
import Pill from '../ui/Pill';
import { EmptyState, ErrorNotice, InfoNotice, SkeletonRows } from '../ui/States';
import { dayLabel } from '../../lib/events';
import { errorMessage, inputClass, money, toCents } from '../../lib/programs';
import { confirmAction } from '../ui/Confirm';
import AddLink from '../ui/AddLink';
import { headRow, th, td, em, num } from './logistics';
import {
  Competition,
  Competitor,
  CompetitorStatus,
  Dignitary,
  InterpretationMode,
  ProgramRecording,
  Slot,
  clock,
  field,
  shortDate,
  useAgenda,
  useProgram,
  useProgramWrite,
} from './agendaTypes';

function Field({
  title,
  hint,
  children,
  wide = false,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={wide ? field.wide : ''}>
      <label className="block">
        <span className={field.label}>{title}</span>
        {children}
      </label>
      {hint ? <p className={field.hint}>{hint}</p> : null}
    </div>
  );
}

/** One fact in a definition list: 13px label above, the value in ink. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-small text-ink-2">{label}</dt>
      <dd className="mt-[2px] text-[15px] leading-[22px] text-ink break-words">{children}</dd>
    </div>
  );
}

const facts = 'grid grid-cols-3 gap-x-6 gap-y-4 max-md:grid-cols-1';
const none = <span className="text-ink-3">–</span>;

const slotLabel = (s: Slot | null) => (s ? `${dayLabel(s.day)}, ${clock(s.startTime)}: ${s.title}` : 'No slot yet');

function SessionPicker({
  eventId,
  value,
  onChange,
}: {
  eventId: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const agenda = useAgenda(eventId);
  return (
    <select className={field.input} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">No slot yet</option>
      {(agenda.data?.sessions ?? []).map((s) => (
        <option key={s.id} value={s.id}>
          {dayLabel(s.day)} {clock(s.startTime)} {s.title}
        </option>
      ))}
    </select>
  );
}

/** Dialog footer: the main action first, then Cancel, then a destructive action at the far right. */
function Footer({
  primary,
  onCancel,
  remove,
  missing,
}: {
  primary: ReactNode;
  onCancel: () => void;
  remove?: ReactNode;
  missing?: string | null;
}) {
  return (
    <>
      {primary}
      <Button onClick={onCancel}>Cancel</Button>
      {missing ? <span className="text-small text-ink-3">{missing}</span> : null}
      {remove ? <span className="ml-auto max-md:ml-0 max-md:w-full max-md:[&>button]:w-full">{remove}</span> : null}
    </>
  );
}

// ---------------------------------------------------------------- interpretation, AV, materials

interface Materials {
  to: string[];
  subject: string;
  text: string;
  missing: number;
  sentAt: string | null;
}

function MaterialsMessage({ eventId, onClose }: { eventId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const msg = useQuery<Materials>(
    ['event-materials', eventId],
    async () => (await api.get(`/events/${eventId}/program/materials`)).data.data,
  );
  const [copied, setCopied] = useState<string | null>(null);
  const sent = useProgramWrite(eventId, () => api.post(`/events/${eventId}/program/materials/sent`));
  const copy = async (what: string, text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(what);
  };
  const m = msg.data;
  const row = 'grid grid-cols-[72px_minmax(0,1fr)_auto] items-center gap-3 py-[10px] border-b border-line';
  return (
    <Modal
      title="Slides message for interpreters and AV"
      why={
        <>
          The platform never sends this. Copy it into your own email, send it, then choose &ldquo;I sent it&rdquo;. Send
          it again whenever new slides come in.
        </>
      }
      onClose={onClose}
      width={760}
      footer={
        <>
          <Button
            variant="primary"
            disabled={!m || sent.isLoading}
            onClick={() =>
              sent.mutate(undefined, {
                onSuccess: () => {
                  qc.invalidateQueries(['event-materials', eventId]);
                  onClose();
                },
              })
            }
          >
            I sent it
          </Button>
          {m?.to.length ? (
            <a
              className="inline-flex items-center justify-center min-h-[44px] px-4 py-[11px] rounded-sm border border-line-strong text-ink font-semibold text-[15px] leading-5 no-underline hover:bg-selected hover:text-ink"
              href={`mailto:${m.to.join(',')}?subject=${encodeURIComponent(m.subject)}`}
            >
              Open my email
            </a>
          ) : null}
          <Button onClick={onClose}>Close</Button>
          <span className="text-small text-ink-3">
            {m?.sentAt ? `Marked sent ${new Date(m.sentAt).toLocaleString('en-CA')}` : 'Not sent yet'}
          </span>
        </>
      }
    >
      {msg.isLoading ? <SkeletonRows rows={5} /> : null}
      {msg.isError ? <ErrorNotice>{errorMessage(msg.error, 'Could not prepare the message')}</ErrorNotice> : null}
      {m ? (
        <div className="text-[15px] leading-[22px]">
          {m.missing ? (
            <div className="mb-3">
              <InfoNotice>
                {m.missing} speaker(s) have no slide link yet: the message says their slides will follow.
              </InfoNotice>
            </div>
          ) : null}
          <div className={`${row} border-t`}>
            <span className="text-ink-2">To</span>
            <span className="min-w-0 break-words text-ink">
              {m.to.length ? (
                m.to.join(', ')
              ) : (
                <span className="text-ink-2">
                  No addresses yet: add the interpreters&rsquo; and AV emails in Settings.
                </span>
              )}
            </span>
            {m.to.length ? (
              <Button size="sm" onClick={() => copy('to', m.to.join(', '))}>
                {copied === 'to' ? 'Copied' : 'Copy'}
              </Button>
            ) : (
              <span />
            )}
          </div>
          <div className={row}>
            <span className="text-ink-2">Subject</span>
            <span className="min-w-0 break-words text-ink">{m.subject}</span>
            <Button size="sm" onClick={() => copy('subject', m.subject)}>
              {copied === 'subject' ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <div className="flex items-end justify-between gap-3 mt-4 mb-[6px]">
            <label htmlFor="materials-text" className={field.label + ' mb-0'}>
              Message
            </label>
            <Button size="sm" onClick={() => copy('text', m.text)}>
              {copied === 'text' ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <textarea
            id="materials-text"
            className={`${field.input} font-mono text-small`}
            rows={14}
            readOnly
            value={m.text}
            onFocus={(e) => e.currentTarget.select()}
          />
        </div>
      ) : null}
    </Modal>
  );
}

/** Interpretation mode, captions, the interpreters' and AV contacts, deadlines, and the materials message. */
export function InterpretationPanel({ eventId, canEdit }: { eventId: string; canEdit: boolean }) {
  const program = useProgram(eventId);
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState(false);
  const p = program.data;
  return (
    <Panel>
      <PanelHeader title="Interpretation, AV and slides">
        {canEdit ? <Button onClick={() => setEditing(true)}>Settings</Button> : null}
        {canEdit ? <Button onClick={() => setMessage(true)}>Prepare the slides message</Button> : null}
      </PanelHeader>
      <PanelBody>
        {program.isLoading ? <SkeletonRows rows={3} /> : null}
        {program.isError ? (
          <ErrorNotice>{errorMessage(program.error, 'Could not load the program')}</ErrorNotice>
        ) : null}
        {p ? (
          <dl className={facts}>
            <Fact label="Interpretation">{p.modes[p.interpretation.mode]}</Fact>
            <Fact label="Live captions on screen">{p.interpretation.captions ? 'Yes, for everyone' : 'No'}</Fact>
            <Fact label="Slides last sent">{p.packageSentAt ? dayLabel(p.packageSentAt.slice(0, 10)) : 'Not yet'}</Fact>
            <Fact label="Interpreters">{p.interpretation.interpreterEmails.join(', ') || none}</Fact>
            <Fact label="AV team">{p.interpretation.avEmails.join(', ') || none}</Fact>
            <Fact label="Speaker deadlines, before their session">
              Draft slides {p.deadlines.draftDeckBusinessDays} business days · final slides {p.deadlines.finalDeckDays}{' '}
              days · prep meeting {p.deadlines.prepMeetingDays} days · details {p.deadlines.detailsDays} days
            </Fact>
          </dl>
        ) : null}
        <p className="text-small text-ink-3 mt-4">
          The interpreter brief and the AV brief are on Documents. Slide and glossary links are entered on each speaker.
        </p>
      </PanelBody>
      {editing && p ? <InterpretationSettings eventId={eventId} onClose={() => setEditing(false)} /> : null}
      {message ? <MaterialsMessage eventId={eventId} onClose={() => setMessage(false)} /> : null}
    </Panel>
  );
}

function InterpretationSettings({ eventId, onClose }: { eventId: string; onClose: () => void }) {
  const p = useProgram(eventId).data;
  const [f, setF] = useState({
    mode: p?.interpretation.mode ?? 'selected',
    captions: p?.interpretation.captions ?? false,
    interpreterEmails: (p?.interpretation.interpreterEmails ?? []).join(', '),
    avEmails: (p?.interpretation.avEmails ?? []).join(', '),
    notes: p?.interpretation.notes ?? '',
    draftDeckBusinessDays: String(p?.deadlines.draftDeckBusinessDays ?? 5),
    finalDeckDays: String(p?.deadlines.finalDeckDays ?? 7),
    prepMeetingDays: String(p?.deadlines.prepMeetingDays ?? 21),
    detailsDays: String(p?.deadlines.detailsDays ?? 14),
  });
  const set = (k: keyof typeof f, v: string | boolean) => setF((x) => ({ ...x, [k]: v }));
  const save = useProgramWrite(eventId, () =>
    api.patch(`/events/${eventId}/program`, {
      interpretation: {
        mode: f.mode,
        captions: f.captions,
        interpreterEmails: f.interpreterEmails,
        avEmails: f.avEmails,
        notes: f.notes,
      },
      deadlines: {
        draftDeckBusinessDays: Number(f.draftDeckBusinessDays),
        finalDeckDays: Number(f.finalDeckDays),
        prepMeetingDays: Number(f.prepMeetingDays),
        detailsDays: Number(f.detailsDays),
      },
    }),
  );
  return (
    <Modal
      title="Interpretation, AV and slides settings"
      why="Deadlines count back from each speaker's first session and appear on their follow-ups."
      onClose={onClose}
      width={680}
      footer={
        <Footer
          onCancel={onClose}
          primary={
            <Button
              variant="primary"
              disabled={save.isLoading}
              onClick={() => save.mutate(undefined, { onSuccess: onClose })}
            >
              Save settings
            </Button>
          }
        />
      }
    >
      {save.isError ? (
        <div className="mb-4">
          <ErrorNotice>{errorMessage(save.error, 'Could not save')}</ErrorNotice>
        </div>
      ) : null}
      <div className={field.grid}>
        <Field title="Interpretation (English / French)" wide>
          <select
            className={field.input}
            value={f.mode}
            onChange={(e) => set('mode', e.target.value as InterpretationMode)}
          >
            {p
              ? Object.entries(p.modes).map(([k, l]) => (
                  <option key={k} value={k}>
                    {l}
                  </option>
                ))
              : null}
          </select>
        </Field>
        <label className={`${field.check} ${field.wide}`}>
          <input
            type="checkbox"
            className={field.box}
            checked={f.captions}
            onChange={(e) => set('captions', e.target.checked)}
          />
          Live captions on screen for everyone
        </label>
        <Field title="Interpreters' emails" hint="Separate with commas.">
          <input
            className={field.input}
            value={f.interpreterEmails}
            onChange={(e) => set('interpreterEmails', e.target.value)}
          />
        </Field>
        <Field title="AV team's emails" hint="Separate with commas.">
          <input className={field.input} value={f.avEmails} onChange={(e) => set('avEmails', e.target.value)} />
        </Field>
        <Field title="Draft slides due" hint="Business days before the session.">
          <input
            className={`${field.input} tabular`}
            inputMode="numeric"
            value={f.draftDeckBusinessDays}
            onChange={(e) => set('draftDeckBusinessDays', e.target.value)}
          />
        </Field>
        <Field title="Final slides due" hint="Days before the session.">
          <input
            className={`${field.input} tabular`}
            inputMode="numeric"
            value={f.finalDeckDays}
            onChange={(e) => set('finalDeckDays', e.target.value)}
          />
        </Field>
        <Field title="Prep meeting with the moderator" hint="Days before the session.">
          <input
            className={`${field.input} tabular`}
            inputMode="numeric"
            value={f.prepMeetingDays}
            onChange={(e) => set('prepMeetingDays', e.target.value)}
          />
        </Field>
        <Field title="Dinner, arrival and consent to record" hint="Days before the session.">
          <input
            className={`${field.input} tabular`}
            inputMode="numeric"
            value={f.detailsDays}
            onChange={(e) => set('detailsDays', e.target.value)}
          />
        </Field>
        <Field title="Notes for the interpreters" hint="Shown on their brief." wide>
          <textarea className={field.input} rows={2} value={f.notes} onChange={(e) => set('notes', e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- dignitaries

function DignitaryModal({ eventId, g, onClose }: { eventId: string; g: Dignitary | null; onClose: () => void }) {
  const [f, setF] = useState({
    name: g?.name ?? '',
    office: g?.office ?? '',
    contact: g?.contact ?? '',
    letterSentOn: g?.letterSentOn ?? '',
    reply: g?.reply ?? 'pending',
    videoReceived: Boolean(g?.videoReceivedAt),
    videoLink: g?.videoLink ?? '',
    minutes: String(g?.minutes ?? 5),
    sessionId: g?.sessionId ?? '',
    notes: g?.notes ?? '',
  });
  const set = (k: keyof typeof f, v: string | boolean) => setF((x) => ({ ...x, [k]: v }));
  const body = {
    ...f,
    letterSentOn: f.letterSentOn || null,
    sessionId: f.sessionId || null,
    minutes: Number(f.minutes),
  };
  const save = useProgramWrite(eventId, () =>
    g
      ? api.patch(`/events/${eventId}/program/dignitaries/${g.id}`, body)
      : api.post(`/events/${eventId}/program/dignitaries`, body),
  );
  const remove = useProgramWrite(eventId, () => api.delete(`/events/${eventId}/program/dignitaries/${g?.id}`));
  return (
    <Modal
      title={g ? `Greeting: ${g.name}` : 'Add a greeting from a dignitary'}
      why="An MP, MPP or other dignitary asked for a 3–5 minute welcome, in person or by video. Its slot places it in the run of show."
      onClose={onClose}
      width={640}
      footer={
        <Footer
          onCancel={onClose}
          missing={f.name.trim() ? null : 'Give their name.'}
          primary={
            <Button
              variant="primary"
              disabled={save.isLoading || !f.name.trim()}
              onClick={() => save.mutate(undefined, { onSuccess: onClose })}
            >
              {g ? 'Save' : 'Add the greeting'}
            </Button>
          }
          remove={
            g ? (
              <Button
                variant="destructive"
                onClick={() => {
                  void confirmAction(`Remove the greeting from ${g.name}?`, 'Remove the greeting').then(
                    (ok) => ok && remove.mutate(undefined, { onSuccess: onClose }),
                  );
                }}
              >
                Remove
              </Button>
            ) : null
          }
        />
      }
    >
      {save.isError || remove.isError ? (
        <div className="mb-4">
          <ErrorNotice>{errorMessage(save.error || remove.error, 'Could not save')}</ErrorNotice>
        </div>
      ) : null}
      <div className={field.grid}>
        <Field title="Name">
          <input className={field.input} value={f.name} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field title="Office" hint="For example: MP for York Centre.">
          <input className={field.input} value={f.office} onChange={(e) => set('office', e.target.value)} />
        </Field>
        <Field title="Their office's contact">
          <input className={field.input} value={f.contact} onChange={(e) => set('contact', e.target.value)} />
        </Field>
        <Field title="Letter sent on">
          <input
            type="date"
            className={field.input}
            value={f.letterSentOn}
            onChange={(e) => set('letterSentOn', e.target.value)}
          />
        </Field>
        <Field title="Reply">
          <select className={field.input} value={f.reply} onChange={(e) => set('reply', e.target.value)}>
            <option value="pending">Waiting for a reply</option>
            <option value="accepted">Will speak in person</option>
            <option value="video">Will send a video</option>
            <option value="declined">Declined</option>
          </select>
        </Field>
        <Field title="Minutes" hint="3–5 asked.">
          <input
            className={`${field.input} tabular`}
            inputMode="numeric"
            value={f.minutes}
            onChange={(e) => set('minutes', e.target.value)}
          />
        </Field>
        <label className={`${field.check} self-end min-h-[44px]`}>
          <input
            type="checkbox"
            className={field.box}
            checked={f.videoReceived}
            onChange={(e) => set('videoReceived', e.target.checked)}
          />
          Video received
        </label>
        <Field title="Video link">
          <input className={field.input} value={f.videoLink} onChange={(e) => set('videoLink', e.target.value)} />
        </Field>
        <Field title="Slot in the agenda" wide>
          <SessionPicker eventId={eventId} value={f.sessionId} onChange={(v) => set('sessionId', v)} />
        </Field>
        <Field title="Notes" wide>
          <textarea className={field.input} rows={2} value={f.notes} onChange={(e) => set('notes', e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

const REPLY_TONE = { pending: 'planned', accepted: 'active', video: 'active', declined: 'inactive' } as const;

/** Greetings from dignitaries: letter, reply, video, slot in the run of show. */
export function DignitariesPanel({ eventId, canEdit }: { eventId: string; canEdit: boolean }) {
  const program = useProgram(eventId);
  const [editing, setEditing] = useState<Dignitary | 'new' | null>(null);
  const list = program.data?.dignitaries ?? [];
  const modal = editing ? (
    <DignitaryModal eventId={eventId} g={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
  ) : null;
  if (program.data && !list.length)
    return canEdit ? (
      <>
        <AddLink label="Greeting from a dignitary" onClick={() => setEditing('new')} />
        {modal}
      </>
    ) : null;
  return (
    <Panel>
      <PanelHeader title="Greetings from dignitaries" note={list.length ? `${list.length}` : undefined}>
        {canEdit ? <Button onClick={() => setEditing('new')}>Add a greeting</Button> : null}
      </PanelHeader>
      <PanelBody>
        {program.isLoading ? <SkeletonRows rows={2} /> : null}
        {program.isError ? (
          <ErrorNotice>{errorMessage(program.error, 'Could not load the greetings')}</ErrorNotice>
        ) : null}
        {list.length ? (
          <div className="overflow-x-auto">
            <table className="w-full tabular min-w-[760px]">
              <thead>
                <tr className={headRow}>
                  <th className={th}>Name and office</th>
                  <th className={th}>Reply</th>
                  <th className={th}>Letter sent</th>
                  <th className={th}>Video</th>
                  <th className={th}>Slot</th>
                  <th className={th}>Next step</th>
                  {canEdit ? (
                    <th className={th}>
                      <span className="sr-only">Edit</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {list.map((g) => (
                  <tr key={g.id} className="border-b border-line hover:bg-selected">
                    <td className={td}>
                      <span className="font-semibold">{g.name}</span>
                      {g.office ? <span className={em}>{g.office}</span> : null}
                    </td>
                    <td className={td}>
                      <Pill tone={REPLY_TONE[g.reply]}>{program.data?.replies[g.reply] ?? g.reply}</Pill>
                    </td>
                    <td className={td}>{g.letterSentOn ? shortDate(g.letterSentOn) : none}</td>
                    <td className={td}>{g.videoReceivedAt ? 'Received' : g.reply === 'video' ? 'Not yet' : none}</td>
                    <td className={td}>
                      {g.slot ? slotLabel(g.slot) : <span className="text-ink-3">No slot yet</span>}
                    </td>
                    <td className={`${td} text-ink-2`}>{g.next ?? none}</td>
                    {canEdit ? (
                      <td className={`${td} text-right`}>
                        <Button size="sm" aria-label={`Edit the greeting from ${g.name}`} onClick={() => setEditing(g)}>
                          Edit
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </PanelBody>
      {modal}
    </Panel>
  );
}

// ---------------------------------------------------------------- competitions

function CompetitionModal({ eventId, c, onClose }: { eventId: string; c: Competition | null; onClose: () => void }) {
  const [f, setF] = useState({
    name: c?.name ?? '',
    description: c?.description ?? '',
    callOpensOn: c?.callOpensOn ?? '',
    deadline: c?.deadline ?? '',
    extendedTo: c?.extendedTo ?? '',
    reminders: (c?.reminders ?? []).map((r) => r.on).join(', '),
    timeCapMinutes: c?.timeCapMinutes ? String(c.timeCapMinutes) : '',
    prize$: c?.prizeCents ? String(c.prizeCents / 100) : '',
    prizeNote: c?.prizeNote ?? '',
    sessionId: c?.sessionId ?? '',
    finalists: c?.finalists ? String(c.finalists) : '',
  });
  const set = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }));
  const body = {
    name: f.name,
    description: f.description,
    callOpensOn: f.callOpensOn || null,
    deadline: f.deadline || null,
    extendedTo: f.extendedTo || null,
    reminders: f.reminders.split(/[\s,;]+/).filter(Boolean),
    timeCapMinutes: f.timeCapMinutes || null,
    prizeCents: toCents(f.prize$),
    prizeNote: f.prizeNote,
    sessionId: f.sessionId || null,
    finalists: f.finalists || null,
  };
  const save = useProgramWrite(eventId, () =>
    c
      ? api.patch(`/events/${eventId}/program/competitions/${c.id}`, body)
      : api.post(`/events/${eventId}/program/competitions`, body),
  );
  const remove = useProgramWrite(eventId, () => api.delete(`/events/${eventId}/program/competitions/${c?.id}`));
  return (
    <Modal
      title={c ? `Competition: ${c.name}` : 'Add a competition'}
      why="An application-based session such as a Dragons' Den: the call, its reminders, the finalists and the prize. The winner's prize becomes an entry owed in the payments ledger."
      onClose={onClose}
      width={680}
      footer={
        <Footer
          onCancel={onClose}
          missing={f.name.trim() ? null : 'Give the competition a name.'}
          primary={
            <Button
              variant="primary"
              disabled={save.isLoading || !f.name.trim()}
              onClick={() => save.mutate(undefined, { onSuccess: onClose })}
            >
              {c ? 'Save' : 'Add the competition'}
            </Button>
          }
          remove={
            c ? (
              <Button
                variant="destructive"
                onClick={() => {
                  void confirmAction(`Remove the competition "${c.name}"?`, 'Remove the competition').then(
                    (ok) => ok && remove.mutate(undefined, { onSuccess: onClose }),
                  );
                }}
              >
                Remove
              </Button>
            ) : null
          }
        />
      }
    >
      {save.isError || remove.isError ? (
        <div className="mb-4">
          <ErrorNotice>{errorMessage(save.error || remove.error, 'Could not save')}</ErrorNotice>
        </div>
      ) : null}
      <div className={field.grid}>
        <Field title="Name" hint="For example: Dragons' Den." wide>
          <input className={field.input} value={f.name} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field title="Description" hint="The call." wide>
          <textarea
            className={field.input}
            rows={3}
            value={f.description}
            onChange={(e) => set('description', e.target.value)}
          />
        </Field>
        <Field title="Call opens">
          <input
            type="date"
            className={field.input}
            value={f.callOpensOn}
            onChange={(e) => set('callOpensOn', e.target.value)}
          />
        </Field>
        <Field title="Deadline">
          <input
            type="date"
            className={field.input}
            value={f.deadline}
            onChange={(e) => set('deadline', e.target.value)}
          />
        </Field>
        <Field title="Deadline extended to">
          <input
            type="date"
            className={field.input}
            value={f.extendedTo}
            onChange={(e) => set('extendedTo', e.target.value)}
          />
        </Field>
        <Field title="Reminder dates" hint="YYYY-MM-DD, separated with commas.">
          <input className={field.input} value={f.reminders} onChange={(e) => set('reminders', e.target.value)} />
        </Field>
        <Field title="Number of finalists">
          <input
            className={`${field.input} tabular`}
            inputMode="numeric"
            value={f.finalists}
            onChange={(e) => set('finalists', e.target.value)}
          />
        </Field>
        <Field title="Minutes per presenter">
          <input
            className={`${field.input} tabular`}
            inputMode="numeric"
            value={f.timeCapMinutes}
            onChange={(e) => set('timeCapMinutes', e.target.value)}
          />
        </Field>
        <Field title="Prize ($)" hint="To the winner.">
          <input
            className={`${field.input} tabular`}
            inputMode="decimal"
            value={f.prize$}
            onChange={(e) => set('prize$', e.target.value)}
          />
        </Field>
        <Field title="Prize note">
          <input className={field.input} value={f.prizeNote} onChange={(e) => set('prizeNote', e.target.value)} />
        </Field>
        <Field title="Session in the agenda" wide>
          <SessionPicker eventId={eventId} value={f.sessionId} onChange={(v) => set('sessionId', v)} />
        </Field>
      </div>
    </Modal>
  );
}

function AddCompetitor({ eventId, c, onClose }: { eventId: string; c: Competition; onClose: () => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [project, setProject] = useState('');
  const add = useProgramWrite(eventId, () =>
    api.post(`/events/${eventId}/program/competitions/${c.id}/competitors`, { email, name, projectTitle: project }),
  );
  return (
    <Modal
      title={`Add an applicant to ${c.name}`}
      why="With an email, the member or contact who has it is used; give a name when the person is new."
      onClose={onClose}
      width={560}
      footer={
        <Footer
          onCancel={onClose}
          missing={email.trim() ? null : 'Give their email.'}
          primary={
            <Button
              variant="primary"
              disabled={!email.trim() || add.isLoading}
              onClick={() => add.mutate(undefined, { onSuccess: onClose })}
            >
              Add the applicant
            </Button>
          }
        />
      }
    >
      {add.isError ? (
        <div className="mb-4">
          <ErrorNotice>{errorMessage(add.error, 'Could not add')}</ErrorNotice>
        </div>
      ) : null}
      <div className={field.grid}>
        <Field title="Email">
          <input className={field.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field title="Name" hint="If they are new.">
          <input className={field.input} value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field title="Project" wide>
          <input className={field.input} value={project} onChange={(e) => setProject(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

function CompetitorRow({
  eventId,
  x,
  statuses,
  canEdit,
}: {
  eventId: string;
  x: Competitor;
  statuses: Record<CompetitorStatus, string>;
  canEdit: boolean;
}) {
  const update = useProgramWrite(eventId, (body: Record<string, unknown>) =>
    api.patch(`/events/program/competitors/${x.id}`, body),
  );
  const remove = useProgramWrite(eventId, () => api.delete(`/events/program/competitors/${x.id}`));
  return (
    <tr className="border-b border-line hover:bg-selected">
      <td className={`${td} ${num} w-[88px]`}>
        {canEdit ? (
          <input
            className={`${field.rowSelect} w-[64px] text-right tabular`}
            aria-label={`Presentation order for ${x.name}`}
            inputMode="numeric"
            defaultValue={x.presentationOrder ?? ''}
            onBlur={(e) => {
              if (e.target.value !== String(x.presentationOrder ?? ''))
                update.mutate({ presentationOrder: e.target.value || null });
            }}
          />
        ) : (
          (x.presentationOrder ?? none)
        )}
      </td>
      <td className={td}>
        <span className="font-semibold">{x.name}</span>
        {x.email ? <span className={em}>{x.email}</span> : null}
      </td>
      <td className={td}>{x.projectTitle ?? none}</td>
      <td className={td}>
        {canEdit ? (
          <select
            className={field.rowSelect}
            aria-label={`Status of ${x.name}`}
            value={x.status}
            onChange={async (e) => {
              const value = e.target.value;
              if (
                value === 'winner' &&
                !(await confirmAction(
                  `Mark ${x.name} as the winner? The prize becomes an entry owed in the payments ledger.`,
                  'Mark as the winner',
                ))
              )
                return;
              update.mutate({ status: value });
            }}
          >
            {Object.entries(statuses).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        ) : (
          <Pill
            tone={
              x.status === 'winner' || x.status === 'finalist'
                ? 'active'
                : x.status === 'applied'
                  ? 'planned'
                  : 'inactive'
            }
          >
            {statuses[x.status] ?? x.status}
          </Pill>
        )}
        {x.paymentEntryId ? <span className={em}>Prize in the ledger</span> : null}
        {update.isError || remove.isError ? (
          <span className="block text-small text-warning">
            {errorMessage(update.error || remove.error, 'Could not save')}
          </span>
        ) : null}
      </td>
      {canEdit ? (
        <td className={`${td} text-right`}>
          <MoreMenu
            label={`More for ${x.name}`}
            items={[
              {
                label: 'Remove',
                destructive: true,
                onClick: () =>
                  void confirmAction(`Remove ${x.name} from the applicants?`, 'Remove the applicant').then(
                    (ok) => ok && remove.mutate(undefined),
                  ),
              },
            ]}
          />
        </td>
      ) : null}
    </tr>
  );
}

function CompetitionBlock({
  eventId,
  c,
  statuses,
  canEdit,
  onEdit,
}: {
  eventId: string;
  c: Competition;
  statuses: Record<CompetitorStatus, string>;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const reminder = useProgramWrite(eventId, (on: string) =>
    api.post(`/events/${eventId}/program/competitions/${c.id}/reminders`, { on }),
  );
  const deadline = c.extendedTo ?? c.deadline;
  return (
    <div className="mt-6 first:mt-0">
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 mb-3">
        <h3 className="font-serif font-semibold text-[18px] leading-[26px] text-ink">{c.name}</h3>
        {canEdit ? (
          <Button size="sm" aria-label={`Edit ${c.name}`} onClick={onEdit}>
            Edit
          </Button>
        ) : null}
      </div>
      <dl className={facts}>
        <Fact label="Call">{c.open ? 'Open' : 'Closed'}</Fact>
        <Fact label="Deadline">
          {deadline ? dayLabel(deadline) : none}
          {c.extendedTo && c.deadline ? <span className={em}>Extended from {dayLabel(c.deadline)}</span> : null}
        </Fact>
        <Fact label="Slot">{slotLabel(c.slot)}</Fact>
        <Fact label="Minutes per presenter">{c.timeCapMinutes ?? none}</Fact>
        <Fact label="Prize">
          {c.prizeCents ? money(c.prizeCents) : none}
          {c.prizeNote ? <span className={em}>{c.prizeNote}</span> : null}
        </Fact>
        <Fact label="Reminders">
          {c.remindersDue.length
            ? c.remindersDue.map((on) => (
                <span key={on} className="block">
                  Due {dayLabel(on)}
                  {canEdit ? (
                    <Button
                      size="sm"
                      variant="quiet"
                      className="ml-2 !min-h-0 !py-0 !px-0"
                      disabled={reminder.isLoading}
                      onClick={() => reminder.mutate(on)}
                    >
                      Mark sent
                    </Button>
                  ) : null}
                </span>
              ))
            : c.nextReminder
              ? `Next ${dayLabel(c.nextReminder)}`
              : none}
        </Fact>
      </dl>
      {reminder.isError ? (
        <p className="text-small text-warning mt-2">{errorMessage(reminder.error, 'Could not mark it sent')}</p>
      ) : null}
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 mt-6 mb-2">
        <h4 className="text-[15px] leading-5 font-semibold text-ink">
          Applicants
          {c.competitors.length ? <span className="font-normal text-ink-3"> · {c.competitors.length}</span> : null}
        </h4>
        {canEdit ? (
          <Button size="sm" onClick={() => setAdding(true)}>
            Add an applicant
          </Button>
        ) : null}
      </div>
      {c.competitors.length ? (
        <table className="w-full tabular">
          <thead>
            <tr className={headRow}>
              <th className={`${th} ${num}`}>Order</th>
              <th className={th}>Applicant</th>
              <th className={th}>Project</th>
              <th className={th}>Status</th>
              {canEdit ? (
                <th className={th}>
                  <span className="sr-only">More</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {c.competitors.map((x) => (
              <CompetitorRow key={x.id} eventId={eventId} x={x} statuses={statuses} canEdit={canEdit} />
            ))}
          </tbody>
        </table>
      ) : c.competitorCounts && Object.keys(c.competitorCounts).length ? (
        <p className="text-[15px] leading-[22px] text-ink">
          {Object.entries(c.competitorCounts)
            .map(([k, n]) => `${n} ${statuses[k as CompetitorStatus] ?? k}`)
            .join(', ')}
        </p>
      ) : (
        <p className="text-[15px] leading-[22px] text-ink-2">No applicants yet.</p>
      )}
      {adding ? <AddCompetitor eventId={eventId} c={c} onClose={() => setAdding(false)} /> : null}
    </div>
  );
}

/** Application-based competitions (Dragons' Den): the call, reminders, applicants, finalists and the prize. */
export function CompetitionsPanel({ eventId, canEdit }: { eventId: string; canEdit: boolean }) {
  const program = useProgram(eventId);
  const [editing, setEditing] = useState<Competition | 'new' | null>(null);
  const list = program.data?.competitions ?? [];
  const statuses = program.data?.competitorStatuses ?? ({} as Record<CompetitorStatus, string>);
  const modal = editing ? (
    <CompetitionModal eventId={eventId} c={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
  ) : null;
  if (program.data && !list.length)
    return canEdit ? (
      <>
        <AddLink label="Competition" onClick={() => setEditing('new')} />
        {modal}
      </>
    ) : null;
  return (
    <Panel>
      <PanelHeader title="Competitions" note={list.length ? `${list.length}` : undefined}>
        {canEdit ? <Button onClick={() => setEditing('new')}>Add a competition</Button> : null}
      </PanelHeader>
      <PanelBody>
        {program.isLoading ? <SkeletonRows rows={2} /> : null}
        {program.isError ? (
          <ErrorNotice>{errorMessage(program.error, 'Could not load the competitions')}</ErrorNotice>
        ) : null}
        {list.map((c, i) => (
          <div key={c.id} className={i ? 'border-t border-line pt-6 mt-8' : ''}>
            <CompetitionBlock
              eventId={eventId}
              c={c}
              statuses={statuses}
              canEdit={canEdit}
              onEdit={() => setEditing(c)}
            />
          </div>
        ))}
      </PanelBody>
      {modal}
    </Panel>
  );
}

// ---------------------------------------------------------------- recordings (after)

function RecordingRow({ eventId, r, canEdit }: { eventId: string; r: ProgramRecording; canEdit: boolean }) {
  const qc = useQueryClient();
  const [link, setLink] = useState(r.recording.link ?? '');
  const [where, setWhere] = useState(r.recording.publishedWhere ?? '');
  const save = useProgramWrite(eventId, async (recording: Record<string, unknown>) => {
    await api.patch(`/events/agenda/sessions/${r.id}`, { recording });
    qc.invalidateQueries(['event-agenda', eventId]);
    return api.get(`/events/${eventId}/program`);
  });
  const rec = r.recording;
  return (
    <tr className="border-b border-line align-top hover:bg-selected">
      <td className={`${td} whitespace-nowrap`}>
        {dayLabel(r.day)}
        <span className={em}>{clock(r.startTime)}</span>
      </td>
      <td className={td}>
        {r.title}
        {r.recordingPlanned ? <span className={em}>planned</span> : null}
      </td>
      <td className={td}>
        <select
          className={field.rowSelect}
          disabled={!canEdit}
          value={rec.recorded === true ? 'yes' : rec.recorded === false ? 'no' : ''}
          onChange={(e) => save.mutate({ recorded: e.target.value === '' ? null : e.target.value === 'yes' })}
        >
          <option value="">?</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      </td>
      <td className={td}>
        <input
          type="checkbox"
          disabled={!canEdit || !rec.recorded}
          checked={Boolean(rec.subtitlesEn)}
          onChange={(e) => save.mutate({ subtitlesEn: e.target.checked })}
        />
      </td>
      <td className={td}>
        <input
          type="checkbox"
          disabled={!canEdit || !rec.recorded}
          checked={Boolean(rec.subtitlesFr)}
          onChange={(e) => save.mutate({ subtitlesFr: e.target.checked })}
        />
      </td>
      <td className={td}>
        {canEdit && rec.recorded ? (
          <div className="flex gap-1">
            <input
              className={`${inputClass} w-[200px]`}
              placeholder="Link"
              value={link}
              onChange={(e) => setLink(e.target.value)}
            />
            <input
              className={`${inputClass} w-[120px]`}
              placeholder="Where (e.g. Moodle)"
              value={where}
              onChange={(e) => setWhere(e.target.value)}
            />
            <Button size="sm" disabled={save.isLoading} onClick={() => save.mutate({ link, publishedWhere: where })}>
              Save
            </Button>
          </div>
        ) : rec.link ? (
          <a className="underline" href={rec.link} target="_blank" rel="noreferrer">
            {rec.publishedWhere || 'link'}
          </a>
        ) : (
          '—'
        )}
        {save.isError ? (
          <div className="text-small text-warning">{errorMessage(save.error, 'Could not save')}</div>
        ) : null}
      </td>
      <td className={td}>
        {r.gaps.length ? <span className="text-warning">{r.gaps.join(', ')}</span> : rec.recorded ? 'Done' : ''}
      </td>
    </tr>
  );
}

/** Each session's recording after the event: recorded, EN/FR subtitles, where it is published. */
export function RecordingsPanel({ eventId, canEdit }: { eventId: string; canEdit: boolean }) {
  const program = useProgram(eventId);
  const list = program.data?.recordings ?? [];
  const open = list.filter((r) => r.gaps.length).length;
  return (
    <Panel>
      <PanelHeader title="Recordings" note={list.length ? `${open} with something left to do` : undefined} />
      <PanelBody>
        {program.isLoading ? <SkeletonRows rows={4} /> : null}
        {program.isError ? (
          <ErrorNotice>{errorMessage(program.error, 'Could not load the recordings')}</ErrorNotice>
        ) : null}
        {program.data && !list.length ? (
          <EmptyState title="No sessions">Sessions come from the agenda.</EmptyState>
        ) : null}
        {list.length ? (
          <div className="overflow-x-auto">
            <table className="w-full tabular">
              <thead>
                <tr className={headRow}>
                  <th className={th}>When</th>
                  <th className={th}>Session</th>
                  <th className={th}>Recorded</th>
                  <th className={th}>EN subtitles</th>
                  <th className={th}>FR subtitles</th>
                  <th className={th}>Published</th>
                  <th className={th}>Left to do</th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <RecordingRow key={r.id} eventId={eventId} r={r} canEdit={canEdit} />
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        <p className="text-small text-ink-3 mt-3">
          Links only: the video and subtitle files stay where they are published.
        </p>
      </PanelBody>
    </Panel>
  );
}
